import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { controlHeightClass } from '@/shared/ui/core/styles';
import type { ControlSize } from '@/shared/ui/core/types';
import { WithTooltip } from '@/shared/ui/core/Tooltip';

export type IconButtonVariant = 'ghost' | 'secondary' | 'danger';

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  /** Accessible name — required for icon-only controls. */
  label: string;
  icon: ReactNode;
  variant?: IconButtonVariant;
  size?: ControlSize;
  /**
   * Show Radix tooltip (preferred over native `title`).
   * Set `false` when nesting inside `DropdownMenuTrigger asChild` / other asChild triggers —
   * wrap `WithTooltip` outside the trigger instead.
   */
  tooltip?: boolean;
  /** Custom classes for the icon wrapper span and SVG. */
  iconClassName?: string;
};

const variantClass: Record<IconButtonVariant, string> = {
  ghost: 'bg-transparent text-text-secondary hover:bg-bg hover:text-text',
  secondary: 'bg-surface text-text border border-border-strong hover:bg-bg',
  danger: 'bg-transparent text-danger-fg hover:bg-danger-soft',
};

const iconBoxClass: Record<ControlSize, string> = {
  sm: 'w-[var(--control-height-dense)]',
  md: 'w-[var(--control-height-md)]',
  lg: 'w-[var(--control-height-default)]',
  touch: 'w-[var(--control-height-touch)]',
};

/** VS3: touch must not force 16px glyphs — scale with control size. */
const iconSvgClass: Record<ControlSize, string> = {
  sm: 'size-3.5 [&>svg]:size-3.5',
  md: 'size-4 [&>svg]:size-4',
  lg: 'size-4 [&>svg]:size-4',
  touch: 'size-5 [&>svg]:size-5',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    className,
    label,
    icon,
    variant = 'ghost',
    size = 'md',
    type = 'button',
    tooltip = true,
    iconClassName,
    ...props
  },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center rounded-control cursor-pointer',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
        'disabled:cursor-not-allowed disabled:text-text-disabled',
        controlHeightClass[size],
        iconBoxClass[size],
        variantClass[variant],
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          'inline-flex items-center justify-center',
          iconSvgClass[size],
          iconClassName,
        )}
        aria-hidden
      >
        {icon}
      </span>
    </button>
  );

  if (!tooltip || size === 'touch') return button;
  return <WithTooltip label={label} openOnClick={false}>{button}</WithTooltip>;
});
